import React, { useState } from 'react';
import styled from 'styled-components';
import { usePermissions } from '../hooks/usePermissions';
import { 
  useGetManagementClientsQuery, 
  useCreateManagementClientMutation,
  useUpdateManagementClientMutation,
  useCycleClientSecretMutation,
  type ManagementClient 
} from '../store/api/managementClientsApi';
import { useGetBusinessSystemsQuery } from '../store/api/businessSystemsApi';
import { Button, Card, Input, Select, EmptyState } from '../styles/components';
import { CreateManClientWizard } from '../components/clients/CreateManClientWizard';
import { ManagementClientEditModal } from '../components/clients/ManagementClientEditModal';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PencilIcon,
  UsersIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const ManagementClientsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
  max-width: 1200px;
  margin: 2rem auto;
  width: 100%;
  padding: 0 2rem;
`;

const ManagementClientsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderContent = styled.div``;

const ManagementClientsTitle = styled.h1`
  font-size: ${props => props.theme.fontSizes['2xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.gray[900]};
`;

const ManagementClientsSubtitle = styled.p`
  margin-top: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.gray[500]};
`;

const CreateButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  
  svg {
    height: 1rem;
    width: 1rem;
  }
`;

const ManagementClientsTable = styled(Card)`
  padding: 0;
`;

const Table = styled.table`
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
`;

const TableHead = styled.thead`
  background-color: ${props => props.theme.colors.gray[50]};
`;

const TableHeader = styled.th`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.xl};
  text-align: left;
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.gray[500]};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const TableBody = styled.tbody`
  background-color: white;
`;

const TableRow = styled.tr`
  border-top: 1px solid ${props => props.theme.colors.gray[200]};
  
  &:hover {
    background-color: ${props => props.theme.colors.gray[50]};
  }
`;

const TableCell = styled.td`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  white-space: nowrap;
`;

const ClientId = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.gray[900]};
`;

const ClientIdSub = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[500]};
`;

const ScopesBadge = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.xs};
  max-width: 200px;
`;

const ScopeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  border-radius: ${props => props.theme.borders.radius.sm};
  background-color: ${props => props.theme.colors.primary[50]};
  color: ${props => props.theme.colors.primary[600]};
  border: 1px solid ${props => props.theme.colors.primary[200]};
`;

const BusinessSystemBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  border-radius: ${props => props.theme.borders.radius.sm};
  background-color: ${props => props.theme.colors.success[50]};
  color: ${props => props.theme.colors.success[600]};
  border: 1px solid ${props => props.theme.colors.success[200]};
`;

const LoadingCard = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 16rem;
  color: ${props => props.theme.colors.gray[500]};
`;

const EmptyCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing['2xl']};
  text-align: center;
  color: ${props => props.theme.colors.gray[500]};
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  
  svg {
    position: absolute;
    left: ${props => props.theme.spacing.sm};
    top: 50%;
    transform: translateY(-50%);
    height: 1.25rem;
    width: 1.25rem;
    color: ${props => props.theme.colors.gray[400]};
  }
`;

const SearchInput = styled(Input)`
  padding-left: ${props => props.theme.spacing.xl};
  width: 100%;
  
  @media (min-width: 768px) {
    width: 20rem;
  }
`;

const FilterSelect = styled(Select)`
  width: 100%;
  
  @media (min-width: 768px) {
    width: 12rem;
  }
`;

const BusinessSystemSelect = styled(Select)`
  width: 100%;
  
  @media (min-width: 768px) {
    width: 15rem;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: between;
  margin-top: ${props => props.theme.spacing.xl};
  padding: ${props => props.theme.spacing.md};
  background: white;
  border-radius: ${props => props.theme.borders.radius.md};
  border: 1px solid ${props => props.theme.colors.gray[200]};
`;

const PaginationInfo = styled.div`
  flex: 1;
  color: ${props => props.theme.colors.gray[500]};
  font-size: ${props => props.theme.fontSizes.sm};
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const PageButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  min-width: 2.5rem;
  height: 2.5rem;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  border-radius: ${props => props.theme.borders.radius.md};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  background: ${props => props.$active ? props.theme.colors.primary[50] : 'white'};
  color: ${props => props.$active ? props.theme.colors.primary[600] : props.theme.colors.gray[500]};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$active ? props.theme.colors.primary[100] : props.theme.colors.gray[50]};
    border-color: ${props => props.$active ? props.theme.colors.primary[300] : props.theme.colors.gray[300]};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    height: 1.25rem;
    width: 1.25rem;
  }
`;

const ResultsCount = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.gray[500]};
  font-size: ${props => props.theme.fontSizes.sm};
  
  svg {
    height: 1rem;
    width: 1rem;
  }
`;

const ActionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  
  svg {
    height: 0.75rem;
    width: 0.75rem;
  }
`;

const ActionsCell = styled(TableCell)`
  display: flex;
  gap: ${props => props.theme.spacing.xs};
  align-items: center;
`;

export function ManagementClients() {
  const { isSupport, isSuperAdmin } = usePermissions();
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    search: '',
    businessSystem: undefined as string | undefined,
  });

  const handleSearchChange = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPage(1);
  };

  const handleBusinessSystemChange = (value: string) => {
    setFilters(prev => ({ ...prev, businessSystem: value || undefined }));
    setPage(1);
  };
  
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const queryParams = {
    page,
    pageSize: 20,
    ...filters,
  };

  const { data: managementClients, isLoading } = useGetManagementClientsQuery(queryParams);
  const { data: businessSystems } = useGetBusinessSystemsQuery();

  // Only show to support users
  if (!isSupport) {
    return (
      <ManagementClientsContainer>
        <EmptyState>
          <ShieldCheckIcon />
          <h3>Access Restricted</h3>
          <p>Management clients are only available to support users.</p>
        </EmptyState>
      </ManagementClientsContainer>
    );
  }

  const handleEditClient = (clientId: string) => {
    setSelectedClientId(clientId);
    setShowEditModal(true);
  };

  if (isLoading) {
    return (
      <ManagementClientsContainer>
        <LoadingCard>Loading management clients...</LoadingCard>
      </ManagementClientsContainer>
    );
  }

  return (
    <ManagementClientsContainer>
      <ManagementClientsHeader>
        <HeaderContent>
          <ManagementClientsTitle>Management Clients</ManagementClientsTitle>
          <ManagementClientsSubtitle>
            Administrators for clients
          </ManagementClientsSubtitle>
        </HeaderContent>
        <CreateButton onClick={() => setShowCreateModal(true)}>
          <PlusIcon />
          Create Client
        </CreateButton>
      </ManagementClientsHeader>

      <FilterContainer>
        <FilterGroup>
          <SearchContainer>
            <MagnifyingGlassIcon />
            <SearchInput
              placeholder="Search by client ID, organization name or ID..."
              value={filters.search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </SearchContainer>
          {isSuperAdmin && (
            <BusinessSystemSelect
              value={filters.businessSystem || ''}
              onChange={(e) => handleBusinessSystemChange(e.target.value)}
            >
              <option value="">All Business Systems</option>
              {(businessSystems || []).map((bs) => (
                <option key={bs.id} value={bs.name}>
                  {bs.name}
                </option>
              ))}
            </BusinessSystemSelect>
          )}
        </FilterGroup>
        
        <ResultsCount>
          <FunnelIcon />
          {managementClients?.totalCount || 0} clients
        </ResultsCount>
      </FilterContainer>
      
      {!managementClients?.items.length ? (
        <EmptyCard>
          <UsersIcon style={{ width: '3rem', height: '3rem', marginBottom: '1rem' }} />
          <p>No management clients found</p>
        </EmptyCard>
      ) : (
        <>
          <ManagementClientsTable>
            <Table>
              <TableHead>
                <tr>
                  <TableHeader>Client ID</TableHeader>
                  <TableHeader>Organization</TableHeader>
                  <TableHeader>Scopes</TableHeader>
                  <TableHeader>Business System</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </tr>
              </TableHead>
              <TableBody>
                {managementClients.items.map((client) => (
                  <TableRow key={client.clientId}>
                    <TableCell>
                      <ClientId>
                        {client.clientId}
                      </ClientId>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div>{client.organizationName || client.organizationId}</div>
                        <ClientIdSub>{client.organizationId}</ClientIdSub>
                      </div>
                    </TableCell>
                    <TableCell>
                      <ScopesBadge>
                        {client.scopes
                          .filter(scope => ['api', 'admin', 'support'].includes(scope))
                          .map((scope, index) => (
                            <ScopeBadge key={index}>{scope}</ScopeBadge>
                          ))}
                      </ScopesBadge>
                    </TableCell>
                    <TableCell>
                      {client.businessSystem ? (
                        <BusinessSystemBadge>{client.businessSystem}</BusinessSystemBadge>
                      ) : (
                        <span style={{ color: '#9CA3AF' }}>-</span>
                      )}
                    </TableCell>
                    <ActionsCell>
                      <ActionButton variant="outline" onClick={() => handleEditClient(client.clientId)}>
                        <PencilIcon />
                        Edit
                      </ActionButton>
                    </ActionsCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ManagementClientsTable>
          
          <PaginationContainer>
            <PaginationInfo>
              Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, managementClients.totalCount)} of {managementClients.totalCount} results
            </PaginationInfo>
            
            <PaginationControls>
              <PageButton
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                <ChevronLeftIcon />
              </PageButton>
              
              {Array.from({ length: Math.min(5, managementClients.totalPages) }, (_, i) => {
                const pageNumber = Math.max(1, page - 2) + i;
                if (pageNumber > managementClients.totalPages) return null;
                
                return (
                  <PageButton
                    key={pageNumber}
                    $active={pageNumber === page}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </PageButton>
                );
              })}
              
              <PageButton
                onClick={() => handlePageChange(page + 1)}
                disabled={page === managementClients.totalPages}
              >
                <ChevronRightIcon />
              </PageButton>
            </PaginationControls>
          </PaginationContainer>
        </>
      )}

      {showCreateModal && (
        <CreateManClientWizard
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            // Refetch will happen automatically due to cache invalidation
          }}
        />
      )}

      {showEditModal && selectedClientId && (
        <ManagementClientEditModal
          clientId={selectedClientId}
          onClose={() => {
            setShowEditModal(false);
            setSelectedClientId(null);
          }}
        />
      )}
    </ManagementClientsContainer>
  );
}